import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Clock, AlertTriangle, UserX, HandHelping, Timer } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { SeverityBadge, StatusBadge } from "../components/badges";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";

function AssignDialog({ open, onClose, onAssign }) {
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState("");
  useEffect(() => {
    if (open) api.get("/users/options").then((r) => setUsers(r.data)).catch(() => {});
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">{t("assign")}</DialogTitle></DialogHeader>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger data-testid="assign-user-select"><SelectValue placeholder="..." /></SelectTrigger>
          <SelectContent>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="assign-confirm-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479]"
          disabled={!selected} onClick={() => onAssign(selected)}>{t("assign")}</Button>
      </DialogContent>
    </Dialog>
  );
}

function SummaryStats({ summary }) {
  const { t } = useLang();
  const stats = [
    { label: t("severity.CRITICAL"), value: summary.critical, color: "text-[#991B1B]", icon: AlertTriangle },
    { label: t("severity.HIGH"), value: summary.high, color: "text-[#EA580C]", icon: AlertTriangle },
    { label: t("severity.MEDIUM"), value: summary.medium, color: "text-[#92400E]", icon: AlertTriangle },
    { label: t("slaBreached"), value: summary.sla_breached, color: "text-[#991B1B]", icon: Timer },
    { label: t("missingReporters"), value: summary.missing_reports, color: "text-[#3730A3]", icon: UserX },
    { label: t("helpNow"), value: summary.help_now, color: "text-[#EA580C]", icon: HandHelping },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {stats.map((st) => (
        <div key={st.label} className="apha-card p-3" data-testid={`ac-stat-${st.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <div className={`text-2xl font-heading font-semibold ${st.color}`}>{st.value}</div>
          <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{st.label}</div>
        </div>
      ))}
    </div>
  );
}

function AiSuggestionsPanel({ suggestions, act }) {
  const { t } = useLang();
  if (suggestions.length === 0) return null;
  return (
    <section className="apha-card border-[#C7D2FE] p-4" data-testid="ai-suggestions-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-[#3730A3]" />
        <h2 className="font-heading font-semibold text-base">{t("aiSuggestions")}</h2>
      </div>
      <div className="space-y-2">
        {suggestions.map((sg) => (
          <div key={sg.id} className="border rounded-md p-3 flex flex-wrap items-center gap-2 justify-between">
            <div className="min-w-0">
              <div className="text-sm">{sg.action}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {sg.project?.key} · {sg.owner_role} · {sg.target_timing}
                {sg.confidence != null && ` · ${t("evidenceConfidence")}: ${Math.round(sg.confidence * 100)}%`}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" data-testid={`suggestion-accept-${sg.id}`} className="bg-[#1E3A8A] hover:bg-[#1c3479]"
                onClick={() => act(() => api.post(`/ai-suggestions/${sg.id}/accept`), t("accept"))}>{t("accept")}</Button>
              <Button size="sm" variant="outline" data-testid={`suggestion-dismiss-${sg.id}`}
                onClick={() => act(() => api.post(`/ai-suggestions/${sg.id}/dismiss`), t("dismiss"))}>{t("dismiss")}</Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ItemActions({ item, act, onAssign }) {
  const { t } = useLang();
  const navigate = useNavigate();
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {item.type === "WARNING" && (
        <>
          {item.status === "NEW" && (
            <Button size="sm" data-testid={`ack-btn-${item.id}`} className="bg-[#1E3A8A] hover:bg-[#1c3479]"
              onClick={() => act(() => api.post(`/warnings/${item.id}/acknowledge`), t("acknowledge"))}>
              {t("acknowledge")}
            </Button>
          )}
          <Button size="sm" variant="outline" data-testid={`assign-btn-${item.id}`}
            onClick={() => onAssign(item.id)}>{t("assign")}</Button>
          <Button size="sm" variant="outline" data-testid={`resolve-btn-${item.id}`}
            onClick={() => act(() => api.post(`/warnings/${item.id}/resolve`), t("resolve"))}>{t("resolve")}</Button>
          <Button size="sm" variant="ghost" data-testid={`review-btn-${item.id}`}
            onClick={() => navigate("/warnings")}>{t("evidence")} →</Button>
        </>
      )}
      {item.type === "MISSING_REPORT" && (
        <>
          <Button size="sm" variant="outline" data-testid={`remind-btn-${item.id}`}
            onClick={() => act(() => api.post(`/expected-reports/${item.id}/remind`), t("remind"))}>{t("remind")}</Button>
          <Button size="sm" variant="ghost" data-testid={`exempt-btn-${item.id}`}
            onClick={() => act(() => api.post(`/expected-reports/${item.id}/exempt`, { reason: "PM exception" }), t("markExempt"))}>
            {t("markExempt")}
          </Button>
        </>
      )}
      {item.type === "ASSISTANCE" && (
        <Button size="sm" data-testid={`accept-assist-btn-${item.id}`} className="bg-[#1E3A8A] hover:bg-[#1c3479]"
          onClick={() => act(() => api.post(`/assistance/${item.id}/accept`), t("acceptOwnership"))}>
          {t("acceptOwnership")}
        </Button>
      )}
      {item.type === "INTERVENTION_OVERDUE" && (
        <Button size="sm" variant="outline" data-testid={`complete-int-btn-${item.id}`}
          onClick={() => act(() => api.post(`/interventions/${item.id}/complete`), t("complete"))}>{t("complete")}</Button>
      )}
    </div>
  );
}

function ActionItemCard({ item, act, onAssign }) {
  const { t } = useLang();
  return (
    <div className="apha-card apha-hover p-4" data-testid={`action-item-${item.type.toLowerCase()}-${item.id}`}>
      <div className="flex flex-wrap items-start gap-2">
        <SeverityBadge severity={item.severity} />
        {item.project && (
          <span className="text-xs font-medium bg-secondary rounded px-1.5 py-0.5">{item.project.key}</span>
        )}
        <span className="text-xs text-muted-foreground rounded px-1.5 py-0.5 border">{item.category}</span>
        {item.sla_breached && (
          <span className="text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded px-1.5 py-0.5 flex items-center gap-1">
            <Clock className="h-3 w-3" /> {t("slaBreached")}
          </span>
        )}
        {item.status && item.type === "WARNING" && <StatusBadge status={item.status} />}
        <span className="ml-auto text-xs text-muted-foreground">
          {t("age")}: {item.age_hours != null ? `${Math.round(item.age_hours)}h` : "—"} · {t("source")}: {item.source}
        </span>
      </div>
      <div className="mt-2 text-sm font-medium">{item.title}</div>
      {item.detail && <div className="text-sm text-muted-foreground mt-0.5">{item.detail}</div>}
      {item.evidence?.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {item.evidence.map((ev) => (
            <li key={`${ev.source_id}-${ev.fact}`} className="text-xs text-muted-foreground flex gap-1.5">
              <span className="text-[#1E3A8A]">•</span>
              <span><span className="font-medium text-foreground">{ev.source_id}</span> — {ev.fact}</span>
            </li>
          ))}
        </ul>
      )}
      {item.recommended_action && (
        <div className="mt-2 text-xs bg-accent/50 border border-[#C7D2FE] rounded px-2 py-1.5 text-[#3730A3]">
          {t("recommendedActions")}: {item.recommended_action}
        </div>
      )}
      <ItemActions item={item} act={act} onAssign={onAssign} />
    </div>
  );
}

export default function ActionCenter() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [assignFor, setAssignFor] = useState(null);

  const load = useCallback(() =>
    api.get("/action-center").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const act = useCallback(async (fn, msg) => {
    try {
      await fn();
      toast.success(msg || "Done");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [load]);

  if (!data)
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-40 w-full" /></div>;

  return (
    <div className="space-y-6" data-testid="action-center-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t("actionCenter")}</h1>
      <SummaryStats summary={data.summary} />
      <AiSuggestionsPanel suggestions={data.ai_suggestions} act={act} />
      <div className="space-y-3">
        {data.items.map((item) => (
          <ActionItemCard key={`${item.type}-${item.id}`} item={item} act={act} onAssign={setAssignFor} />
        ))}
        {data.items.length === 0 && <div className="apha-card p-8 text-sm text-muted-foreground">{t("noData")}</div>}
      </div>
      <AssignDialog open={!!assignFor} onClose={() => setAssignFor(null)}
        onAssign={(uid) => { act(() => api.post(`/warnings/${assignFor}/assign`, { owner_user_id: uid }), t("assign")); setAssignFor(null); }} />
    </div>
  );
}
